/**
 * Structural diff primitives. Domain-agnostic: callers supply records,
 * key extraction, equality, and any skipping policy. No field names,
 * no formatting, no rendering live here.
 */

export type FieldDiffKind = "change" | "add" | "remove";

export interface FieldDiff {
  key: string;
  kind: FieldDiffKind;
  /** Raw value on the before side (absent for "add"). */
  before?: unknown;
  /** Raw value on the after side (absent for "remove"). */
  after?: unknown;
}

export const deepEqual = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a) === JSON.stringify(b);

interface DiffRecordOptions {
  /** Return true to exclude a key from the diff entirely. */
  skip?: (key: string) => boolean;
  /** Equality for paired values. Default: deepEqual. */
  equal?: (a: unknown, b: unknown) => boolean;
}

/**
 * Diff two (possibly missing) records over their key union.
 * Emits one FieldDiff per differing key; raw values only.
 */
export const diffRecord = (
  before: Record<string, unknown> | undefined | null,
  after: Record<string, unknown> | undefined | null,
  opts: DiffRecordOptions = {},
): FieldDiff[] => {
  const equal = opts.equal ?? deepEqual;
  const beforeRecord = before ?? {};
  const afterRecord = after ?? {};

  const keys = Array.from(
    new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)]),
  );

  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    if (opts.skip?.(key)) continue;

    const inBefore = key in beforeRecord;
    const inAfter = key in afterRecord;

    if (!inBefore && inAfter) {
      diffs.push({ key, kind: "add", after: afterRecord[key] });
      continue;
    }
    if (inBefore && !inAfter) {
      diffs.push({ key, kind: "remove", before: beforeRecord[key] });
      continue;
    }
    if (!equal(beforeRecord[key], afterRecord[key])) {
      diffs.push({
        key,
        kind: "change",
        before: beforeRecord[key],
        after: afterRecord[key],
      });
    }
  }
  return diffs;
};

export interface ArrayDiffResult<T> {
  /** Present in `after` only, in after-order. */
  added: T[];
  /** Present in `before` only, in before-order. */
  removed: T[];
  /** Paired items whose values differ, in after-order. */
  changed: Array<{ key: string; before: T; after: T }>;
}

interface DiffArrayOptions<T> {
  /** Equality for paired items. Default: deepEqual. */
  equal?: (a: T, b: T) => boolean;
}

/**
 * Diff two (possibly missing) arrays by identity key. Items whose
 * keyOf collides within one array: last one wins (Map semantics).
 */
export const diffArrayByKey = <T>(
  before: T[] | undefined | null,
  after: T[] | undefined | null,
  keyOf: (item: T) => string,
  opts: DiffArrayOptions<T> = {},
): ArrayDiffResult<T> => {
  const equal = opts.equal ?? deepEqual;
  // Coerce non-arrays (e.g. "" from empty old_value) to empty.
  const beforeArr = Array.isArray(before) ? before : [];
  const afterArr = Array.isArray(after) ? after : [];

  const beforeMap = new Map(beforeArr.map((item) => [keyOf(item), item]));
  const afterMap = new Map(afterArr.map((item) => [keyOf(item), item]));

  const added = afterArr.filter((item) => !beforeMap.has(keyOf(item)));
  const removed = beforeArr.filter((item) => !afterMap.has(keyOf(item)));
  const changed = afterArr
    .filter((item) => {
      const prev = beforeMap.get(keyOf(item));
      return prev !== undefined && !equal(prev, item);
    })
    .map((item) => ({
      key: keyOf(item),
      before: beforeMap.get(keyOf(item)) as T,
      after: item,
    }));

  return { added, removed, changed };
};
